import { useCallback, useEffect, useRef } from 'react';
import { PaymentMethodEnum, ProviderEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import { getProviderFromPaymentMethod } from '@/utils/paymentParams';
import { getStripePaymentKeyBySelectedPaymentMethod } from '@/utils/stripe';
import {
  isStripeSuccessResponse,
  isStripeFailedResponse,
  isStripeRedirectResponse,
  isNuveiSuccessResponse,
  isBrainTreeSuccessResponse,
  isAdyenFinalizePaymentResponse,
  isAdyenRedirectResponse,
  isPaypalSuccessResponse,
  isNuveiFailedResponse,
  isNuveiPendingResponse,
  isNuveiRedirectResponse,
  isAdyenFailedResponse,
  isAdyenSuccessResponse,
  isPaypalFailedResponse,
  isBrainTreeFailedResponse,
  isBrainTreeRedirectResponse,
  getManagedOrderToken,
  getPaymentAttemptId,
  isForter3DSChallengeRequired,
} from '@/types/guards';
import type { PaymentKeys, PaymentMethod } from '@/types/checkout';
import { AppchargeErrorType, type BasePaymentResponse } from '@/types/payment';
import type { RedirectPaymentContext, StripeApmNextActionState, ThreeDSSecurityState } from '@/types/context';
import { getRedirectPaymentData } from '@/utils/redirectPayment';
import { getStripePostFinalizeAction, StripePostFinalizeAction } from '@/utils/stripeActions';
import { getRawPaymentFailureMessage } from '@/utils/error';
import { isUnhandledPaymentResult } from '@/utils/paymentResult';
import { getCurrentDomain, LocationRoutes } from '@/utils/location';
import { isMobileDevice } from '@/utils/device';
import { adyenService } from '@/services/adyenService';
import { adyenExpressService } from '@/services/adyenExpressService';
import { adyenApplePayService } from '@/services/adyenApplePayService';
import { logInfo, logError } from '@/utils/logger';
import { InfoLogs, ErrorLogs } from '@/constants/logs';
import { stripeInPageNextActionPaymentMethods, stripePaymentHandlerTabMethods } from '@/constants/stripe';

interface UsePaymentResultsParams {
  paymentMethods: PaymentMethod[];
  onSuccess: (result: BasePaymentResponse, provider: ProviderEnum) => void;
  onPending: (result: BasePaymentResponse, provider: ProviderEnum) => void;
  onError: (appChargeErrorType?: AppchargeErrorType, provider?: ProviderEnum, rawReason?: string) => void;
  onRedirect: (redirectData: RedirectPaymentContext) => void;
  on3DSRequired: (state: ThreeDSSecurityState) => void;
  // Dedicated channel for generic in-page APMs (e.g. Bizum), kept separate from
  // card/wallet 3DS (`on3DSRequired`). Drives <StripeApmNextActionForm/>.
  onStripeApmAction: (state: StripeApmNextActionState) => void;
  actions: {
    stopPaymentProcess: () => void;
    // Discards the blank tab redirect APMs pre-open on the click gesture. Safe
    // to call unconditionally: a no-op when no tab was opened (wallets/cards).
    closeRedirectTab: () => void;
  };
}

interface UsePaymentResultsReturn {
  handlePaymentResults: (
    results: unknown,
    selectedPaymentMethod: ExcludeOtherPaymentMethod,
    paymentKeys: PaymentKeys
  ) => Promise<unknown>;
}

export const usePaymentResults = ({
  paymentMethods,
  onSuccess,
  onPending,
  onError,
  onRedirect,
  on3DSRequired,
  onStripeApmAction,
  actions,
}: UsePaymentResultsParams): UsePaymentResultsReturn => {
  // Pointer to the latest `handlePaymentResults` so the Forter
  // `handleThreeDSCompleteResponse` callback (captured in React state at
  // 3DS-required time) can re-enter the dispatch pipeline with the
  // `/3ds/complete` response. Synced via the effect at the bottom of the
  // hook. The indirection is isolated to this single callback so the
  // PSP-native branches stay self-contained.
  const handlePaymentResultsRef = useRef<UsePaymentResultsReturn['handlePaymentResults']>(() =>
    Promise.resolve(undefined)
  );

  const handleStripeResponse = useCallback(
    async (
      results: unknown,
      provider: ProviderEnum,
      selectedPaymentMethod: PaymentMethodEnum,
      stripePaymentKey: string
    ) => {
      if (isStripeSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
        return;
      }

      // Keep this BEFORE the redirect check: `isStripeFailedResponse` also
      // matches when `appChargeErrorType` is set regardless of status, and the
      // original ordering prioritized surfacing that failure over a redirect.
      if (isStripeFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider, getRawPaymentFailureMessage(results));
        return;
      }

      if (isStripeRedirectResponse(results, provider)) {
        const redirectData = await getRedirectPaymentData(results, ProviderEnum.STRIPE);
        let redirectUrl = redirectData.redirectUrl;
        // Route through the top-level PAYMENT_HANDLER tab (Stripe's own
        // handleNextAction, hosted at top level) instead of running it inside the
        // checkout iframe. CASH_APP / UPI need this only on mobile (desktop keeps
        // the in-page QR); Swish needs it on ALL devices — its next_action's app
        // deep link would otherwise navigate the iframe (see
        // `stripePaymentHandlerTabMethods`).
        const usesPaymentHandlerTab =
          stripePaymentHandlerTabMethods.includes(selectedPaymentMethod) ||
          (stripeInPageNextActionPaymentMethods.includes(selectedPaymentMethod) && isMobileDevice());
        if (usesPaymentHandlerTab && stripePaymentKey) {
          redirectUrl = `${getCurrentDomain()}/${LocationRoutes.PAYMENT_HANDLER}/${redirectData.clientSecret}/${stripePaymentKey}`;
        }

        // Classify what this `status: redirect` response actually needs and
        // route each kind to its OWN flow (see `getStripePostFinalizeAction`):
        //   - ThreeDS       → card/wallet 3DS via the `threeDSSecurityState`
        //                     channel (<StripeCard3DSForm/>).
        //   - ApmNextAction → generic in-page APMs (e.g. Bizum) via the
        //                     separate `stripeApmAction` channel
        //                     (<StripeApmNextActionForm/>).
        //   - Redirect / InPageQr → the hosted `onRedirect` path (Swish takes
        //                     this via the PAYMENT_HANDLER-tab rewrite above).
        const postFinalizeAction = getStripePostFinalizeAction({ paymentMethod: selectedPaymentMethod, redirectUrl });

        if (postFinalizeAction === StripePostFinalizeAction.ThreeDS) {
          // Card + wallet 3DS — the untouched, card/wallet-only channel.
          // `closeRedirectTab` is a harmless no-op here (cards/wallets never
          // pre-open a tab).
          actions.closeRedirectTab();
          on3DSRequired({
            show3DSForm: true,
            provider: ProviderEnum.STRIPE,
            // Carry the *actual* method (incl. express GPay/ApplePay) so the
            // 3DS driver can pick the correct Stripe instance for multi-MID
            // publishers. Context's `paymentMethodSelected` is unreliable here
            // (stays on default for express flows).
            params: results,
            paymentMethod: selectedPaymentMethod,
            handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
            // Prefer the type produced by `handleNextAction` (live decline
            // reason) over the original response's, which is typically absent on
            // a `requires_action` payload.
            handlePaymentError: (appChargeErrorTypeFromAction?: AppchargeErrorType) => {
              actions.stopPaymentProcess();
              onError(
                appChargeErrorTypeFromAction ?? results?.appChargeErrorType,
                provider,
                getRawPaymentFailureMessage(results)
              );
            },
          });
          return;
        }

        if (postFinalizeAction === StripePostFinalizeAction.ApmNextAction) {
          // Generic in-page APMs (e.g. Bizum) — their OWN channel, fully
          // separate from 3DS. These methods no longer pre-open a tab
          // (`isRequireToOpenTab` returns false), so `closeRedirectTab` only
          // cleans up the conservative unknown-APM fallback.
          actions.closeRedirectTab();
          onStripeApmAction({
            active: true,
            params: results,
            paymentMethod: selectedPaymentMethod,
            handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
            handlePaymentError: (appChargeErrorTypeFromAction?: AppchargeErrorType) => {
              actions.stopPaymentProcess();
              onError(
                appChargeErrorTypeFromAction ?? results?.appChargeErrorType,
                provider,
                getRawPaymentFailureMessage(results)
              );
            },
          });
          return;
        }
        onRedirect({
          ...redirectData,
          redirectUrl,
          showRedirect: true,
          paymentMethod: selectedPaymentMethod,
          orderId: results.orderId,
          handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
        });
        return;
      }

      // Catch-all for an unrecognized / malformed Stripe response that matched
      // none of the success / failed / redirect (3DS) guards above. Previously
      // such a response returned silently, leaving `isInPaymentProcess` stuck
      // `true` and the ProcessingLoader frozen with no way to retry except a
      // full page refresh. Release the flag and surface a recoverable error so
      // the shopper can simply pay again. Success / failed / redirect / 3DS all
      // return before reaching here, so their behavior is unchanged.
      actions.stopPaymentProcess();
      onError(undefined, provider);
    },
    [onSuccess, onError, onRedirect, on3DSRequired, onStripeApmAction, actions]
  );

  const handleNuveiResponse = useCallback(
    async (results: unknown, provider: ProviderEnum) => {
      if (isNuveiSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
        return;
      }
      if (isNuveiFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider, getRawPaymentFailureMessage(results));
        return;
      }
      if (isNuveiPendingResponse(results, provider)) {
        onPending(results as BasePaymentResponse, provider);
        return;
      }
      if (isNuveiRedirectResponse(results, provider)) {
        const redirectData = await getRedirectPaymentData(results, ProviderEnum.NUVEI);
        onRedirect({
          ...redirectData,
          clientSecret: results.sessionToken,
          provider: ProviderEnum.NUVEI,
          handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
        });
        return;
      }
    },
    [onSuccess, onPending, onError, onRedirect, actions]
  );

  const handleBraintreeResponse = useCallback(
    async (results: unknown, provider: ProviderEnum) => {
      if (isBrainTreeSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
      }
      if (isBrainTreeFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider, getRawPaymentFailureMessage(results));
      }
      if (isBrainTreeRedirectResponse(results, provider)) {
        const redirectData = await getRedirectPaymentData(results, ProviderEnum.BRAINTREE);
        onRedirect({
          ...redirectData,
          provider: ProviderEnum.BRAINTREE,
          handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
        });
      }
    },
    [onSuccess, onError, onRedirect, actions]
  );

  const handleAdyenResponse = useCallback(
    async (results: unknown, provider: ProviderEnum, selectedPaymentMethod: PaymentMethodEnum) => {
      if (isAdyenFinalizePaymentResponse(results)) {
        if (isAdyenRedirectResponse(results)) {
          // Wallets in Adyen (GPay / ApplePay) need `createFromAction` to
          // mount the 3DS2 component — same path cards already use. Without
          // this gate they fell through to `<RedirectPayment/>` and stuck.
          const requiresAdyenAction =
            selectedPaymentMethod === PaymentMethodEnum.CREDIT_CARD ||
            selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY ||
            selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY;

          if (requiresAdyenAction) {
            // Express wallets (GPay/ApplePay) keep their native sheet open
            // until the original `onSubmit` SubmitActions resolves. Adyen's
            // native 3DS2 chain renders a challenge modal on our page, but
            // the wallet sheet sits on top and blocks interaction, so the
            // user can never complete the challenge and the sheet times
            // out with REQUEST_TIMEOUT.
            //
            // Per Adyen guidance (Adyen/adyen-web#3315), since we render 3DS
            // in our own container via `createFromAction`, resolve the
            // wallet's SubmitActions with an empty object. This closes the
            // sheet AND prevents the SDK from auto-mounting its own 3DS2
            // component (which would race with ours). The real payment
            // result flows through `/status` after the challenge completes.
            if (selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY) {
              adyenExpressService.dismissGooglePaySheet();
            } else if (selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY) {
              adyenApplePayService.dismissApplePaySheet();
            }

            // The last step before the challenge component exists (ACDEV-26410).
            // Without it, a session that returns an action and never mounts is
            // unattributable: reaching here and not mounting means the component
            // failed to come up, while never reaching here means the response was
            // routed somewhere else entirely. For wallets it also dates the sheet
            // dismissal, so the handoff can be read against the moment this page
            // came back to the foreground.
            logInfo(InfoLogs.adyen3dsHandoffRequested, {
              paymentMethod: selectedPaymentMethod,
              actionType: results.adyen?.action?.type ?? 'none',
              actionSubtype: (results.adyen?.action as { subtype?: string } | undefined)?.subtype ?? 'none',
              visibilityState: document.visibilityState,
            });

            on3DSRequired({
              show3DSForm: true,
              provider: ProviderEnum.ADYEN,
              params: results,
              paymentMethod: selectedPaymentMethod,
              handlePaymentSuccess: () => {
                onSuccess(results as BasePaymentResponse, provider);
              },
              // Prefer the challenge-time reason (`/status` resultCode or an
              // SDK component error) over the pre-challenge response, which is
              // a `requires_action` payload and carries no failure detail.
              handlePaymentError: (_, rawReasonFrom3DS) => {
                actions.stopPaymentProcess();
                onError(
                  results?.appChargeErrorType,
                  provider,
                  rawReasonFrom3DS ?? getRawPaymentFailureMessage(results)
                );
              },
            });
          } else {
            const redirectData = await getRedirectPaymentData(results, ProviderEnum.ADYEN);
            onRedirect({
              ...redirectData,
              provider: ProviderEnum.ADYEN,
              handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
            });
          }
        }
        if (isAdyenFailedResponse(results)) {
          actions.stopPaymentProcess();
          if (selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY) {
            adyenExpressService.completeGooglePayPayment(false);
          } else if (selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY) {
            adyenApplePayService.completeApplePayPayment(false);
          } else {
            adyenService.runPaymentFailed();
          }
          onError(results?.appChargeErrorType, provider, getRawPaymentFailureMessage(results));
        }
        if (isAdyenSuccessResponse(results)) {
          if (selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY) {
            adyenExpressService.completeGooglePayPayment(true, results.adyen.resultCode);
          } else if (selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY) {
            adyenApplePayService.completeApplePayPayment(true, results.adyen.resultCode);
          }
          onSuccess(results as BasePaymentResponse, provider);
        }
      }
    },
    [onSuccess, onError, on3DSRequired, onRedirect, actions]
  );

  const handlePaypalResponse = useCallback(
    async (results: unknown, provider: ProviderEnum) => {
      if (isPaypalSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
      }
      if (isPaypalFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider, getRawPaymentFailureMessage(results));
      }
    },
    [onSuccess, onError, actions]
  );

  // Forter-orchestrated 3DS — fires whenever `isForter3DSChallengeRequired`
  // matches the finalize response (token + REDIRECT status in the same PSP
  // container), independent of the PSP. Mirrors the Adyen 3DS-required
  // branch: hand context to <ForterCard3DSForm/> and provide thin success /
  // error bridges. The form owns the Forter SDK challenge AND the follow-up
  // `/3ds/complete` POST (just as <AdyenCard3DSForm/> owns its own `/status`
  // call). The `/3ds/complete` response has the same shape as
  // `/finalize-payment` (success / failure / redirect / nested 3DS), so the
  // form pipes it back through `handleThreeDSCompleteResponse`, which re-enters
  // this hook's dispatch pipeline — keeping PSP routing in one place.
  // `handlePaymentSuccess` remains as the simple Adyen-style resume on the
  // original response, used only as the safety net when the form has no
  // `/3ds/complete` payload to dispatch (e.g. missing `paymentAttemptId`).
  //
  // Caller contract: only invoke when `isForter3DSChallengeRequired(results)`
  // is true. The token / paymentAttemptId are pulled from `results` directly
  // so the function stays self-contained and the orchestrator's call site is
  // a single one-liner.
  const handleForter3DSResponse = useCallback(
    (
      results: unknown,
      provider: ProviderEnum,
      selectedPaymentMethod: ExcludeOtherPaymentMethod,
      paymentKeys: PaymentKeys
    ) => {
      on3DSRequired({
        show3DSForm: true,
        provider,
        managedOrderToken: getManagedOrderToken(results),
        paymentAttemptId: getPaymentAttemptId(results),
        paymentMethod: selectedPaymentMethod,
        params: results,
        handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
        handlePaymentError: (appChargeErrorTypeFromChallenge?: AppchargeErrorType) => {
          actions.stopPaymentProcess();
          onError(
            appChargeErrorTypeFromChallenge ??
              (results as { appChargeErrorType?: AppchargeErrorType })?.appChargeErrorType,
            provider,
            getRawPaymentFailureMessage(results)
          );
        },
        // Shopper closed the challenge. Same reset as a redirect walk-away:
        // release Pay and hide the modal, with no decline copy and no
        // PAYMENT_INTENT_FAILED (those live on `onError`).
        handlePaymentCancel: () => {
          actions.stopPaymentProcess();
          on3DSRequired({ show3DSForm: false });
        },
        handleThreeDSCompleteResponse: (response: unknown) =>
          handlePaymentResultsRef.current(response, selectedPaymentMethod, paymentKeys),
      });
    },
    [onSuccess, onError, on3DSRequired, actions]
  );

  const handlePaymentResults = useCallback(
    async (
      results: unknown,
      selectedPaymentMethod: ExcludeOtherPaymentMethod,
      paymentKeys: PaymentKeys
    ): Promise<unknown> => {
      const resultsStripe = (results as { stripe?: Record<string, unknown> })?.stripe;
      logInfo(InfoLogs.checkoutPaymentProcessed, {
        selectedPaymentMethod,
        results: resultsStripe
          ? { ...(results as Record<string, unknown>), stripe: { ...resultsStripe, clientSecret: undefined } }
          : results,
      });
      const provider = getProviderFromPaymentMethod(selectedPaymentMethod, paymentMethods);

      // Forter-orchestrated 3DS takes precedence over the PSP-native flows.
      // Returning here skips the PSP dispatch and the unhandled-result safety
      // net below — both run again on the `/3ds/complete` response once
      // <ForterCard3DSForm/> pipes it back through `handlePaymentResults` via
      // `handleThreeDSCompleteResponse`.
      if (isForter3DSChallengeRequired(results)) {
        // Same contract as the Adyen native-3DS branch below: the express
        // wallet sheet stays open (and dies with CALLBACK_TIMED_OUT after
        // ~30s) until its SubmitActions settle, and it sits ON TOP of the
        // challenge modal. Resolve it with an empty object before handing off
        // to <ForterCard3DSForm/>. No-op for card flows / non-Adyen wallets
        // (no pending actions).
        if (selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY) {
          adyenExpressService.dismissGooglePaySheet();
        } else if (selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY) {
          adyenApplePayService.dismissApplePaySheet();
        }
        handleForter3DSResponse(results, provider, selectedPaymentMethod, paymentKeys);
        return results;
      }

      switch (provider) {
        case ProviderEnum.PAYPAL:
          await handlePaypalResponse(results, provider);
          break;
        case ProviderEnum.STRIPE:
          await handleStripeResponse(
            results,
            provider,
            selectedPaymentMethod,
            getStripePaymentKeyBySelectedPaymentMethod(paymentMethods, selectedPaymentMethod, paymentKeys) || ''
          );
          break;

        case ProviderEnum.NUVEI:
          await handleNuveiResponse(results, provider);
          break;

        case ProviderEnum.BRAINTREE:
          handleBraintreeResponse(results, provider);
          break;

        case ProviderEnum.ADYEN:
          handleAdyenResponse(results, provider, selectedPaymentMethod);
          break;

        default:
          break;
      }

      // Safety net (no provider logic lives here): if the response shape
      // matched none of the dispatched provider's success / failure / redirect
      // / 3DS guards, the handler took no terminal action and the shopper would
      // be stuck on a frozen ProcessingLoader. Release it and surface a
      // recoverable error. `isUnhandledPaymentResult` is a pure read-only
      // composition of the existing guards and is the single source of truth
      // for this decision; Stripe self-handles via its own catch-all so it is
      // excluded there to avoid double-firing. See utils/paymentResult.ts.
      if (isUnhandledPaymentResult(results, provider)) {
        logError(ErrorLogs.checkoutPaymentError, undefined, {
          provider,
          selectedPaymentMethod,
          reason: 'unhandled_payment_result',
        });
        // Releasing the loader is not enough for express wallets: the native
        // sheet waits on its SubmitActions and, left unsettled, dies with
        // CALLBACK_TIMED_OUT ~30s later, after which every further click is
        // answered with Google's CANCELED ("called while the payment sheet was
        // already shown"). Reject so Google gets `transactionState: 'ERROR'`
        // and the sheet surfaces a dismissable error alongside the recoverable
        // one we show. No-op when no actions are pending (non-Adyen flows).
        if (selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY) {
          adyenExpressService.completeGooglePayPayment(false);
        } else if (selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY) {
          adyenApplePayService.completeApplePayPayment(false);
        }
        actions.stopPaymentProcess();
        onError(undefined, provider);
      }

      return results;
    },
    [
      paymentMethods,
      handleStripeResponse,
      handleNuveiResponse,
      handleBraintreeResponse,
      handleAdyenResponse,
      handlePaypalResponse,
      handleForter3DSResponse,
      actions,
      onError,
    ]
  );

  useEffect(() => {
    handlePaymentResultsRef.current = handlePaymentResults;
  }, [handlePaymentResults]);

  return {
    handlePaymentResults,
  };
};
